import React from 'react';
import { useHistory } from 'react-router';
import { getDupontAnalysis } from 'api/urls';
import { apiFetch } from 'api/api';
import { numberWithCommas } from 'utils/Utils';
import SectionError from 'components/error/SectionError';

/* ── design tokens ── */
const T = {
    bg:   '#F6F5F0',
    card: '#FFFFFF',
    line: '#E5E2D8',
    ink:  '#232821',
    mut:  '#6E766C',
    acc:  '#2E9E7A',
    bar:  '#81C7A8',
    barDim: '#C8E6D8',
};

/* ── Mini sparkline bar chart ── */
const Sparkline = ({ values, latestIndex, dates, suffix }) => {
    const max = Math.max(...values.map(v => parseFloat(v) || 0));
    const [hoveredIdx, setHoveredIdx] = React.useState(null);
    return (
        <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 3,
            height: 48,
            marginTop: 'auto',
            paddingTop: 8,
        }}>
            {values.map((v, i) => {
                const h = max > 0 ? Math.max((parseFloat(v) / max) * 100, 6) : 6;
                const isLatest = i === latestIndex;
                const isHovered = hoveredIdx === i;
                const year = dates && dates[i] ? 'FY' + String(dates[i]).substring(2, 4) : `Y${i + 1}`;
                return (
                    <div
                        key={i}
                        onMouseEnter={() => setHoveredIdx(i)}
                        onMouseLeave={() => setHoveredIdx(null)}
                        style={{
                            flex: 1,
                            height: `${h}%`,
                            background: isHovered ? T.acc : T.bar,
                            borderRadius: 3,
                            transition: 'background 0.15s',
                            cursor: 'pointer',
                            position: 'relative',
                        }}
                    >
                        {isHovered && (
                            <div style={{
                                position: 'absolute',
                                bottom: 'calc(100% + 6px)',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: T.ink,
                                color: '#fff',
                                borderRadius: 6,
                                padding: '5px 8px',
                                fontSize: 10.5,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 30,
                                lineHeight: 1.5,
                                pointerEvents: 'none',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                            }}>
                                <div style={{ color: T.bar, fontWeight: 700, marginBottom: 2 }}>{year}</div>
                                <div>{numberWithCommas(parseFloat(v).toFixed(2))}{suffix}</div>
                                {/* Arrow */}
                                <div style={{
                                    position: 'absolute',
                                    bottom: -4,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '4px solid transparent',
                                    borderRight: '4px solid transparent',
                                    borderTop: `4px solid ${T.ink}`,
                                }} />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/* ── Single metric mini-card ── */
const MetricCard = ({ label, values, suffix, latestIndex, dates }) => {
    const latest = values.length > 0 ? parseFloat(values[values.length - 1]) : null;
    return (
        <div style={{
            background: T.bg,
            borderRadius: 12,
            padding: '14px 16px',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
            }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: T.mut, lineHeight: 1.3 }}>
                    {label}
                </span>
                {latest !== null && (
                    <span style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: T.ink,
                        whiteSpace: 'nowrap',
                        marginLeft: 8,
                    }}>
                        {numberWithCommas(latest.toFixed(suffix === '×' ? 2 : 1))}{suffix}
                    </span>
                )}
            </div>
            <Sparkline values={values} latestIndex={latestIndex} dates={dates} suffix={suffix} />
        </div>
    );
};

/* ── Main DuPont component ── */
const DuPont = (props) => {
    const history = useHistory();
    const [data,       setData]       = React.useState([]);
    const [fetchError, setFetchError] = React.useState(null);
    const [retryTick,  setRetryTick]  = React.useState(0);

    React.useEffect(() => {
        const fetch = async () => {
            setFetchError(null);
            const { data: res, error } = await apiFetch({ url: getDupontAnalysis(history.location.search) });
            if (error) { setFetchError(error); return; }
            setData(res ?? []);
        };
        fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history.location.search, retryTick]);

    const profitability = data.map(d => d.profitability);
    const productivity  = data.map(d => d.productivity);
    const leverage      = data.map(d => d.leverage);
    const dates         = data.map(d => d.as_on_date);
    const latestIdx     = data.length - 1;

    const latestRoe     = data.length > 0 ? parseFloat(data[latestIdx].roe).toFixed(1) : '--';

    return (
        <div {...props} style={{ marginBottom: 16 }}>
            <div style={{
                background: T.card,
                borderRadius: 16,
                border: `1px solid ${T.line}`,
                padding: '18px 20px',
            }}>
                {/* Header row */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 16,
                    flexWrap: 'wrap',
                }}>
                    <span style={{
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontSize: 16,
                        fontWeight: 700,
                        color: T.ink,
                    }}>
                        DuPont analysis
                    </span>
                    <span style={{
                        fontSize: 12,
                        color: T.mut,
                        fontWeight: 500,
                    }}>
                        ROE = Margin × Turnover × Leverage =&nbsp;
                        <strong style={{ color: T.ink }}>{latestRoe}%</strong>
                    </span>
                </div>

                {/* Three mini-cards */}
                {data.length > 0 ? (
                    <div style={{ display: 'flex', gap: 12 }}>
                        <MetricCard
                            label="Net Profit Margin"
                            values={profitability}
                            suffix="%"
                            latestIndex={latestIdx}
                            dates={dates}
                        />
                        <MetricCard
                            label="Asset Turnover"
                            values={productivity}
                            suffix="×"
                            latestIndex={latestIdx}
                            dates={dates}
                        />
                        <MetricCard
                            label="Financial Leverage"
                            values={leverage}
                            suffix="×"
                            latestIndex={latestIdx}
                            dates={dates}
                        />
                    </div>
                ) : fetchError ? (
                    <SectionError message={fetchError.message} onRetry={() => setRetryTick(t => t + 1)} />
                ) : (
                    <div style={{ color: T.mut, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                        Loading…
                    </div>
                )}
            </div>
        </div>
    );
};

export default DuPont;
